import { TERMS_AND_CONDITIONS, PRIVACY_POLICY } from '../constants/legalDocuments';
import type { ConsentRepository } from '../ports/ConsentRepository';
import type { HashGenerator } from '../ports/HashGenerator';
import type { ProcessConsentInput, ProcessConsentOutput } from '../dtos/ProcessConsentDTO';
import type { DocumentType } from '../../domain/entities/LegalDocument';

/**
 * Use case: Process consent after successful login
 * Follows dependency inversion - receives ports as parameters
 */
export const processConsentAfterLogin = async (
  input: ProcessConsentInput,
  dependencies: {
    consentRepository: ConsentRepository;
    hashGenerator: HashGenerator;
  },
): Promise<ProcessConsentOutput> => {
  const { userId, deviceInfo, sessionId } = input;
  const { consentRepository, hashGenerator } = dependencies;

  try {
    const [termsHash, privacyHash] = await Promise.all([
      hashGenerator.generateHash(TERMS_AND_CONDITIONS),
      hashGenerator.generateHash(PRIVACY_POLICY),
    ]);

    const documents: Array<{ type: DocumentType; version: string; hash: string }> = [
      {
        type: 'terms_conditions',
        version: TERMS_AND_CONDITIONS.version,
        hash: termsHash,
      },
      {
        type: 'privacy_policy',
        version: PRIVACY_POLICY.version,
        hash: privacyHash,
      },
    ];

    const result = await consentRepository.recordConsent({
      userId,
      documents,
      deviceInfo,
      sessionId,
    });

    return {
      success: true,
      newConsentRecorded: result.recordedDocuments.length > 0,
    };
  } catch (error) {
    return {
      success: false,
      newConsentRecorded: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};
